import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '~/services/auth';
import { router } from '~/routes';
import { browserAdapter } from '~/services/browserAdapter';
import { initializeAppRuntime } from '~/services/appRuntime';
import { registerUrlChangeInvalidation, registerAuthChangeInvalidation } from '~/lib/query/plugins';
import { registerBookingResetOnLogout } from '~/services/internalApi/booking/context/resetOnLogout';
import { createQueryClient } from '~/lib/query/client';
import { isDevelopment, SHOW_DEV_TOOLS } from '~/config/env';
import { ErrorBoundary } from '~/lib/error/ErrorBoundary';
import { registerVersionToast } from '@butter/ui/patterns';
import { captureMessage } from '@butter/monitoring/web';
import './index.css';

// When a stale-deploy chunk fails to load (a tab left open across a deploy), prompt a
// refresh via a cancelable auto-refresh countdown instead of nuking in-flight state.
// The reporter fires only when a reload didn't resolve it (a genuinely broken deploy);
// the message text avoids "Failed to fetch" so Sentry's ignoreErrors won't drop it.
registerVersionToast({
  onBrokenDeploy: () =>
    // "did not resolve" (not "broken deploy"): a repeat failure after reload can also be a
    // flaky network. `online` tags it for triage; message avoids Sentry's ignoreErrors terms.
    captureMessage('Stale-chunk recovery: reload did not resolve stale chunk', 'warning', {
      tags: { area: 'app-version-recovery', online: navigator.onLine },
    }),
});

// Initialize LocatorJS in development only (Alt/Option + click to open component in editor)
if (import.meta.env.DEV) {
  import('@locator/runtime').then((locator) => locator.default());
}

// Create React Query client with standardized caching configuration
// See ~/lib/query/cachePresets.ts for cache preset documentation
const queryClient = createQueryClient();

/**
 * Initialize and render the app
 * 
 * CRITICAL: browserAdapter MUST be initialized before React renders
 * Otherwise, AuthProvider will try to read tokens from the default
 * memory adapter (which is empty) before it switches to localStorage
 */
async function initializeAndRender() {
  
  // 0. Start the DevTools auth-scenario mock worker. Gated on SHOW_DEV_TOOLS —
  // the exact same condition as the DevTools panel — so the worker never runs
  // without the UI that controls it. Transparent until a scenario is picked; must
  // start before any API request so its handlers are registered.
  //
  // The leading env check is what keeps MSW out of the production bundle: Vite
  // inlines import.meta.env.* as literals, so in a prod build this folds to
  // `false && …` and Rollup drops the dynamic import (and all of msw) entirely.
  // SHOW_DEV_TOOLS alone can't do that — it comes from a zod safeParse, so it's
  // an opaque runtime value Rollup must preserve. Fails closed: an unset
  // VITE_ENVIRONMENT also folds to false.
  if ((import.meta.env.DEV || import.meta.env.VITE_ENVIRONMENT === 'staging') && SHOW_DEV_TOOLS) {
    try {
      const { startAuthMockWorker } = await import('~/features/dev-tools/mocks');
      await startAuthMockWorker();
    } catch (err) {
      // Never fatal. This runs inside initializeAndRender(), whose .catch()
      // replaces the page with "Failed to Initialize" — so an unguarded throw
      // here would let a dev-only mock worker take the whole app down. Service
      // worker registration fails for mundane reasons (private browsing, browser
      // policy); degrade to "no mocks", never to a dead site.
      console.warn('[DevTools] auth mock worker failed to start; continuing without mocks', err);
    }
  }

  // 1. Initialize browser adapter FIRST (async, must wait!)
  // This detects storage capabilities and selects the best adapter
  // (native localStorage > parent bridge > memory fallback)
  await browserAdapter.initialize();
  
  // 2. Initialize app runtime (monitoring, query params)
  initializeAppRuntime({
    sentryDsn: import.meta.env.VITE_SENTRY_DSN,
    // VITE_ENVIRONMENT comes from fly.toml build args (staging/production).
    // MODE is always "production" in deploys (the pipeline runs plain `vite build`),
    // so it only serves as the local-dev fallback ("development").
    environment: import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE,
    appName: 'web-portal-2',
    debug: isDevelopment
  });
  
  // 3. Register automatic query invalidation on URL parameter changes
  // This bridges appRuntime (URL params) with React Query (cache invalidation)
  const cleanupUrlInvalidation = registerUrlChangeInvalidation(queryClient);
  const cleanupAuthInvalidation = registerAuthChangeInvalidation(queryClient);
  const cleanupBookingReset = registerBookingResetOnLogout();
  
  // Handle cleanup on HMR (Hot Module Replacement) in development
  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      cleanupUrlInvalidation();
      cleanupAuthInvalidation();
      cleanupBookingReset();
    });
  }
  
  // 4. Render React app (AuthProvider can now read from correct storage)
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <RouterProvider router={router} />
          </AuthProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </StrictMode>,
  );
}

// Start initialization
initializeAndRender().catch((error) => {
  console.error('[App] ❌ Failed to initialize app:', error);
  // ErrorBoundary will handle initialization errors, but show a fallback just in case
  const root = document.getElementById('root');
  if (root) {
    root.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; height: 100vh; font-family: system-ui;">
        <div style="text-align: center; max-width: 400px; padding: 2rem; background: white; border-radius: 0.5rem; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
          <h1 style="color: #dc2626; margin-bottom: 1rem;">Failed to Initialize</h1>
          <p style="color: #6b7280; margin-bottom: 1.5rem;">
            The application failed to start. Please refresh the page.
          </p>
          <button onclick="window.location.reload()" style="background: #2563eb; color: white; padding: 0.5rem 1rem; border: none; border-radius: 0.375rem; cursor: pointer;">
            Refresh Page
          </button>
        </div>
      </div>
    `;
  }
});
